/**
 * Occupations List Page
 * Main page for listing and managing occupations
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as RiskIcon,
  Factory as SourceIcon,
  Science as ExamIcon,
  FileDownload as ExportIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ListAlt as FichaIcon,
  Warning as WarningIcon,
  Restaurant as FoodHandlingIcon,
  ElectricBolt as ElectricalIcon,
  Elevator as PemtIcon,
  Compress as ConfinedSpaceIcon,
  Height as HeightWorkIcon,
  LocalFireDepartment as HotWorkIcon,
  Forklift as ForkliftIcon,
  Agriculture as SelfPropelledMachineIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';

// Components
import { OccupationFilters } from './components';
import {
  PageShell,
  DataTable,
  type DataTableColumn,
  AlertBadge,
  generateOccupationAlerts,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';

// Services and types
import {
  occupationService,
  Occupation,
  OccupationFilters as OccupationFiltersType,
  SpecialActivityType,
  SPECIAL_ACTIVITIES,
  OccupationSpecialActivity,
} from '../../services/occupationService';
import { companyService } from '../../services/companyService';
import { departmentService } from '../../services/departmentService';
import { contarRiscosDosCargos, type ContagemPorCargo } from '../../services/riscosDoCargoService';
import { getApiErrorMessage } from '../../types/common';
import { enderecoDeAplicar } from './enderecoDeAplicar';

// Hooks
import { useAcao, useControle } from '../../authorization/useVeredito';
import { useOccupationExport } from '../../hooks/useOccupationExport';

interface Company {
  id: number;
  tradeName: string;
  legalName: string;
}

interface Department {
  id: number;
  name: string;
  companyId: number;
}

interface OccupationFiltersData {
  searchTerm: string;
  companyId: string;
  departmentId: string;
  specialActivities: string[];
}

// Map special activity type to MUI icon and color (same pattern as DepartmentTable safety icons)
const ACTIVITY_ICON_MAP: Record<string, { icon: typeof HeightWorkIcon; color: string }> = {
  [SpecialActivityType.HEIGHT_WORK]: { icon: HeightWorkIcon, color: 'purple' },
  [SpecialActivityType.CONFINED_SPACE]: { icon: ConfinedSpaceIcon, color: 'red' },
  [SpecialActivityType.ELECTRICAL]: { icon: ElectricalIcon, color: 'yellow' },
  [SpecialActivityType.FORKLIFT]: { icon: ForkliftIcon, color: 'green' },
  [SpecialActivityType.FOOD_HANDLING]: { icon: FoodHandlingIcon, color: 'orange' },
  [SpecialActivityType.PEMT]: { icon: PemtIcon, color: 'blue' },
  [SpecialActivityType.HOT_WORK]: { icon: HotWorkIcon, color: 'red' },
  [SpecialActivityType.SELF_PROPELLED_MACHINE]: { icon: SelfPropelledMachineIcon, color: 'gray' },
};

// Get activity icons array for an occupation
const getActivityIcons = (activities?: OccupationSpecialActivity[]) => {
  if (!activities || activities.length === 0) return [];

  return activities.map((item) => {
    const config = SPECIAL_ACTIVITIES.find(a => a.value === item.activity);
    const iconConfig = ACTIVITY_ICON_MAP[item.activity];
    return {
      icon: iconConfig?.icon || WarningIcon,
      label: config?.label || item.activity,
      color: iconConfig?.color || 'gray',
      key: item.id,
    };
  });
};

/** Texto e borda no tom escuro de cada cor: o tom principal não chega a 4,5:1 sobre o branco. */
const CONTRASTE_WARNING = { color: 'warning.dark', borderColor: 'warning.dark' } as const;
const CONTRASTE_INFO = { color: 'info.dark', borderColor: 'info.dark' } as const;
const CONTRASTE_SUCCESS = { color: 'success.dark', borderColor: 'success.dark' } as const;

// Get alerts for an occupation
const getOccupationAlerts = (occupation: Occupation) => {
  if (!occupation._counts) return [];
  return generateOccupationAlerts({
    employeeCount: occupation._counts.employees,
    riskApplicationCount: occupation._counts.riskApplications,
    sourceApplicationCount: occupation._counts.sourceApplications,
    examApplicationCount: occupation._counts.examApplications,
  });
};

const columns: DataTableColumn<Occupation>[] = [
  {
    id: 'name',
    label: 'Nome do Cargo',
    primary: true,
    render: (occupation) => (
      <>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Link
            component={RouterLink}
            to={`/occupations/${occupation.id}`}
            variant="body2"
            fontWeight={500}
            underline="hover"
          >
            {occupation.name}
          </Link>
          <AlertBadge alerts={getOccupationAlerts(occupation)} />
        </Box>
        {occupation.role && (
          <Typography variant="caption" color="text.secondary">
            {occupation.role}
          </Typography>
        )}
      </>
    ),
  },
  {
    id: 'cboCode',
    label: 'CBO',
    render: (occupation) => (
      <Typography variant="body2">
        {occupation.cboCode ?? '-'}
      </Typography>
    ),
  },
  {
    id: 'department',
    label: 'Setor',
    render: (occupation) => (
      <Typography variant="body2">
        {occupation.department?.name || '-'}
      </Typography>
    ),
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (occupation) => (
      <Typography variant="body2">
        {occupation.company?.tradeName || occupation.company?.legalName || '-'}
      </Typography>
    ),
  },
  {
    id: 'specialActivities',
    label: 'Atividades Especiais',
    render: (occupation) => {
      const icons = getActivityIcons(occupation.specialActivities);
      if (icons.length === 0) return '-';
      return (
        <Box display="flex" flexWrap="wrap" gap={0.5} alignItems="center">
          {icons.slice(0, 3).map((activity) => (
            <Tooltip key={activity.key} title={activity.label}>
              <activity.icon
                fontSize="small"
                sx={{ color: activity.color }}
              />
            </Tooltip>
          ))}
          {icons.length > 3 && (
            <Typography variant="caption" color="text.secondary">
              +{icons.length - 3}
            </Typography>
          )}
        </Box>
      );
    },
  },
];

/** "14 de cima · 1 próprio": de onde vêm os riscos que valem para o cargo. */
function origemDosRiscos(herdados: number, proprios: number): string {
  const partes: string[] = [];
  if (herdados > 0) partes.push(`${herdados} de cima`);
  if (proprios > 0) partes.push(`${proprios} ${proprios === 1 ? 'próprio' : 'próprios'}`);
  return partes.join(' · ');
}

/** Sem a contagem (falhou ou ainda não chegou), a coluna mostra traço: zero seria afirmar que não há risco. */
function colunaDeRiscos(contagem: ContagemPorCargo | null): DataTableColumn<Occupation> {
  return {
    id: 'riscos',
    label: 'Riscos',
    render: (occupation) => {
      const doCargo = contagem?.[String(occupation.id)];
      if (!doCargo) {
        return <Typography variant="body2">—</Typography>;
      }
      const origem = origemDosRiscos(doCargo.herdados, doCargo.proprios);
      return (
        <>
          <Typography variant="body2" fontWeight={500}>
            {doCargo.total}
          </Typography>
          {origem && (
            <Typography variant="caption" color="text.secondary">
              {origem}
            </Typography>
          )}
        </>
      );
    },
  };
}

const OccupationsListPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // "Concluir", na aplicação, volta para cá.
  const aqui = `${location.pathname}${location.search}`;
  const { enqueueSnackbar } = useSnackbar();
  const editar = useControle('/occupations/:id/edit', pode('occupation.update'), 'Editar');
  const excluir = useAcao('occupation.delete', 'Excluir');
  const { exporting, exportOccupations } = useOccupationExport();

  // State
  const [occupations, setOccupations] = useState<Occupation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentFilters, setCurrentFilters] = useState<Partial<OccupationFiltersType>>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [contagemDeRiscos, setContagemDeRiscos] = useState<ContagemPorCargo | null>(null);

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [occupationToDelete, setOccupationToDelete] = useState<Occupation | null>(null);

  // Companies and departments for filters
  const [companies, setCompanies] = useState<Company[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  // Load companies and departments for filters
  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [companiesRes, departmentsRes] = await Promise.all([
          companyService.getCompanies({ page: 1, limit: 1000 }),
          departmentService.getDepartments({ page: 1, limit: 1000 })
        ]);

        setCompanies(companiesRes.data.map(c => ({
          id: Number(c.id),
          tradeName: c.tradeName || c.legalName,
          legalName: c.legalName
        })));

        setDepartments(departmentsRes.data.map(d => ({
          id: Number(d.id),
          name: d.name,
          companyId: Number(d.companyId)
        })));
      } catch (error) {
        console.error('Error loading filter data:', error);
      }
    };

    loadFilterData();
  }, []);

  // Load occupations from API
  // Só a resposta do último pedido entra — a página e a contagem dela: a que chega atrasada é de
  // outra página, e o paginador já está noutra.
  const ultimoPedido = useRef(0);
  const loadOccupations = useCallback(async (
    pageNumber: number,
    limit: number,
    search: string,
    filters: Partial<OccupationFiltersType>
  ) => {
    const estePedido = ++ultimoPedido.current;
    const eDoUltimoPedido = () => estePedido === ultimoPedido.current;
    setLoading(true);
    setError(null);

    try {
      const response = await occupationService.getOccupations({
        page: pageNumber,
        limit,
        search,
        filters
      });
      if (!eDoUltimoPedido()) return;

      setOccupations(response.data);
      setTotalCount(response.pagination.total);
      setContagemDeRiscos(null);
      // Uma chamada para a página inteira (E2), nunca uma por linha.
      contarRiscosDosCargos(response.data.map((occupation) => occupation.id))
        .then((contagem) => eDoUltimoPedido() && setContagemDeRiscos(contagem))
        .catch(() => eDoUltimoPedido() && setContagemDeRiscos(null));
    } catch (err) {
      if (!eDoUltimoPedido()) return;
      console.error('Error loading occupations:', err);
      setError(getApiErrorMessage(err, 'Erro ao carregar cargos'));
      enqueueSnackbar('Erro ao carregar cargos', { variant: 'error' });
    } finally {
      if (eDoUltimoPedido()) setLoading(false);
    }
  }, [enqueueSnackbar]);

  // Apply filters
  const applyFilters = (filters: OccupationFiltersData) => {
    const apiFilters: Partial<OccupationFiltersType> = {};

    if (filters.companyId) {
      apiFilters.companyId = Number(filters.companyId);
    }

    if (filters.departmentId) {
      apiFilters.departmentId = Number(filters.departmentId);
    }

    if (filters.specialActivities.length > 0) {
      apiFilters.specialActivities = filters.specialActivities as SpecialActivityType[];
    }

    setCurrentFilters(apiFilters);
    setSearchTerm(filters.searchTerm);
    setPage(1);

    loadOccupations(1, rowsPerPage, filters.searchTerm, apiFilters);
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    setPage(newPage + 1);
    loadOccupations(newPage + 1, rowsPerPage, searchTerm, currentFilters);
  };

  // Handle rows per page change
  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
    loadOccupations(1, newRowsPerPage, searchTerm, currentFilters);
  };

  // Handle edit occupation
  const handleEditOccupation = (occupation: Occupation) => {
    navigate(`/occupations/${occupation.id}/edit`);
  };

  // Handle delete occupation
  const handleDeleteOccupation = async (occupation: Occupation) => {
    try {
      await occupationService.deleteOccupation(occupation.id);
      enqueueSnackbar('Cargo excluído com sucesso', { variant: 'success' });
      loadOccupations(page, rowsPerPage, searchTerm, currentFilters);
    } catch (err) {
      console.error('Error deleting occupation:', err);
      enqueueSnackbar(getApiErrorMessage(err, 'Erro ao excluir cargo'), { variant: 'error' });
    }
  };

  // Handle delete click
  const handleDeleteClick = (occupation: Occupation) => {
    setOccupationToDelete(occupation);
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleDeleteConfirm = () => {
    if (occupationToDelete) {
      handleDeleteOccupation(occupationToDelete);
    }
    setDeleteDialogOpen(false);
    setOccupationToDelete(null);
  };

  // Cancel delete
  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setOccupationToDelete(null);
  };

  // Load data on mount
  useEffect(() => {
    loadOccupations(1, 10, '', {});
  }, [loadOccupations]);

  // Convert for table display (MUI Table uses 0-based pagination)
  const displayPage = page - 1;

  const filtersActive = searchTerm.trim() !== '' || Object.keys(currentFilters).length > 0;
  const colunas = useMemo(() => {
    const comRiscos = [...columns];
    comRiscos.splice(1, 0, colunaDeRiscos(contagemDeRiscos));
    return comRiscos;
  }, [contagemDeRiscos]);
  const aplicarEm = (caminho: string) => navigate(enderecoDeAplicar(caminho, currentFilters, aqui));

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Cargos' },
      ]}
      title="Cargos"
      actions={
        <>
          <RouteActionButton
            para="/risk-applications"
            exige={pode('risk_application.read', { perspectiva: 'plataforma' })}
            aoAgir={() => aplicarEm('/risk-applications')}
            variant="outlined"
            color="warning"
            sx={CONTRASTE_WARNING}
            size="small"
            startIcon={<RiskIcon />}
          >
            Aplicar Riscos
          </RouteActionButton>
          <RouteActionButton
            para="/source-applications"
            exige={pode('source_application.read', { perspectiva: 'plataforma' })}
            aoAgir={() => aplicarEm('/source-applications')}
            variant="outlined"
            color="info"
            sx={CONTRASTE_INFO}
            size="small"
            startIcon={<SourceIcon />}
          >
            Aplicar Fontes
          </RouteActionButton>
          <RouteActionButton
            para="/exam-applications"
            exige={pode('exam_application.read', { perspectiva: 'plataforma' })}
            aoAgir={() => aplicarEm('/exam-applications')}
            variant="outlined"
            color="success"
            sx={CONTRASTE_SUCCESS}
            size="small"
            startIcon={<ExamIcon />}
          >
            Aplicar Exames
          </RouteActionButton>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={() => exportOccupations(currentFilters)}
            disabled={loading || exporting}
          >
            Exportar
          </Button>
          <RouteActionButton
            para="/occupations/create"
            exige={pode('occupation.create')}
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
          >
            Novo Cargo
          </RouteActionButton>
        </>
      }
    >
      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Box sx={{ mb: 3 }}>
        <OccupationFilters
          onFilter={applyFilters}
          companies={companies}
          departments={departments}
          loading={loading || exporting}
          total={totalCount}
        />
      </Box>

      {/* Occupations Table */}
      <DataTable
        columns={colunas}
        rows={occupations}
        getRowId={(occupation) => occupation.id}
        loading={loading}
        emptyState={{
          title: 'Nenhum cargo cadastrado ainda',
          description:
            'Cargos descrevem a função de cada funcionário e definem os exames exigidos no PCMSO.',
          prereqChain: 'occupations',
          action: {
            label: 'Cadastrar cargo',
            node: 'occupations',
            deniedHint: 'Peça ao administrador da empresa para cadastrar um cargo.',
          },
        }}
        filtersActive={filtersActive}
        actions={[
          {
            icon: <FichaIcon fontSize="small" />,
            tooltip: 'Riscos, fontes e exames do cargo',
            onClick: (occupation) => navigate(`/occupations/${occupation.id}`),
          },
          {
            icon: <EditIcon fontSize="small" />,
            onClick: handleEditOccupation,
            ...editar,
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: handleDeleteClick,
            ...excluir,
          },
        ]}
        pagination={{
          page: displayPage,
          rowsPerPage,
          totalCount,
          onPageChange: handlePageChange,
          onRowsPerPageChange: handleRowsPerPageChange,
        }}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Confirmar Exclusão
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o cargo <strong>{occupationToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita. Funcionários vinculados a este cargo impedirão a exclusão.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancelar</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default OccupationsListPage;
